import React, { useContext, useState } from "react";
import Row from "@amzn/meridian/row";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Link from "@amzn/meridian/link";
import Column from "@amzn/meridian/column";
import FullScreen from "react-full-screen";
import { getDocumentURL } from "../../handler/ParentDataHandler";
import { FormattedMessage } from "react-intl";
import { OutboundConfigContext } from "../../outboundAmzl/context/OutboundConfigContext";
import { CapabilityContext } from "../../overview/context/CapabilityContext";
import { AR_V2, SSD } from "../../outboundAmzl/util/Constants";

import {
  isRouteStalled,
  getOverviewStatusPriority,
  isRouteAtRiskARv2,
  ASSIGNABLE,
  IN_PROGRESS,
  PENDING_PICK,
  PENDING_STAGE,
  PENDING_DEPART,
  NOT_READY,
  STAGED,
  PICKED,
  DEPARTED,
} from "../../outboundAmzl/RouteStatusManager";
import { useDeviceLayout } from "../../common/context/DeviceLayoutContext";

const VisualGridViewARV2 = (props) => {
  const { spacingInset_shorthand2 } = useDeviceLayout();
  const [screenState, setScreenState] = useState(false);
  const { outboundConfigModel } = useContext(OutboundConfigContext);
  const { capability } = useContext(CapabilityContext);
  const pageName = capability.data === AR_V2 ? "outbound" : "outboundAMZL";

  const toggleScreen = () => {
    setScreenState(!screenState);
  };

  let routeList = props.routeList;
  routeList = routeList.sort((a, b) => a.criticalPullTime - b.criticalPullTime);
  const routeWaveList = [];
  if (SSD !== capability.data) {
    routeList.forEach((route) => {
      const wave = routeWaveList.find((wave) => {
        return wave.name === route.routeSLA;
      });
      if (wave) {
        wave.routeList.push(route);
      } else {
        routeWaveList.push({
          name: route.routeSLA,
          routeList: [route],
        });
      }
    });
  }

  // sort route list of each wave by status.
  routeWaveList.forEach((wave) => {
    wave.routeList.sort((a, b) => {
      const statusPriorityA = getOverviewStatusPriority(a.overviewStatus);
      const statusPriorityB = getOverviewStatusPriority(b.overviewStatus);
      if (statusPriorityA === statusPriorityB) {
        return 0;
      } else if (statusPriorityA < statusPriorityB) {
        return -1;
      } else {
        return 1;
      }
    });
  });

  const getRouteColor = (status) => {
    switch (status) {
      case ASSIGNABLE:
        return "#BBC0C1";
      case NOT_READY:
      case PENDING_PICK:
      case IN_PROGRESS:
        return "#FAD000";
      case PICKED:
      case PENDING_STAGE:
        return "#FF714D";
      case STAGED:
      case PENDING_DEPART:
        return "#47C2EC";
      case DEPARTED:
        return "#98C91E";
      default:
        return "#BBC0C1";
    }
  };

  function naIfDefault(value) {
    return !value || value === "..." ? "NA" : value;
  }

  const onRouteClick = (route) => {
    getDocumentURL().then((result) => {
      const params = [
        "Overview",
        naIfDefault(route.routeId),
        naIfDefault(encodeURIComponent(route.rswsStatus)),
        naIfDefault(encodeURIComponent(route.stagingLocation)),
        naIfDefault(route.hazmatWeight),
        naIfDefault(route.picker),
        naIfDefault(route.routeCode),
        naIfDefault(route.stationCode),
      ];
      const url = new URL(result.replace(pageName, "pick"));
      const shareableLink = "/routeDetail/" + params.join("/");
      url.searchParams.set("shareableLink", shareableLink);
      window.open(url.href, "_blank");
    });
  };

  const getDisplayProgress = (route) => {
    switch (route.overviewStatus) {
      case ASSIGNABLE:
      case IN_PROGRESS:
      case PENDING_PICK:
        return getPickingProgress(route);
      case PICKED:
      case PENDING_STAGE:
        return (
          <span>
            Stage Progress: <b>{route.stageProgress}</b>
            <br />
          </span>
        );
      case STAGED:
      case PENDING_DEPART:
      case DEPARTED:
        return (
          <span>
            Depart Progress: <b>{route.departProgress}</b>
            <br />
          </span>
        );
      default:
        return "";
    }
  };

  const getPickingProgress = (route) => {
    return (
      <span>
        Pick Progress: <b>{route.pickProgress}</b>
        <br />
      </span>
    );
  };

  const boxWidth = 16;
  const boxHeight = 16;
  const partialIconSrc =
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMjIgMjIiPjxjaXJjbGUgY3g9IjExIiBjeT0iLTEwNDEuMzYiIHI9IjgiIHRyYW5zZm9ybT0ibWF0cml4KDEgMCAwLTEgMC0xMDMwLjM2KSIgb3BhY2l0eT0iLjk4IiBmaWxsPSIjZGE0NDUzIi8+PHBhdGggZD0ibS0yNi4zMDkgMTguMDdjLTEuMTggMC0yLjEzNS45NjgtMi4xMzUgMi4xMjl2MTIuODJjMCAxLjE3Ni45NDggMi4xMjkgMi4xMzUgMi4xMjkgMS4xODMgMCAyLjEzNS0uOTY4IDIuMTM1LTIuMTI5di0xMi44MmMwLTEuMTc2LS45NDYtMi4xMjktMi4xMzUtMi4xMjl6bTAgMjEuMzQ4Yy0xLjE4IDAtMi4xMzUuOTU0LTIuMTM1IDIuMTM1IDAgMS4xOC45NTQgMi4xMzUgMi4xMzUgMi4xMzUgMS4xODEgMCAyLjEzNS0uOTU0IDIuMTM1LTIuMTM1IDAtMS4xOC0uOTUyLTIuMTM1LTIuMTM1LTIuMTM1eiIgdHJhbnNmb3JtPSJtYXRyaXgoLjMwMDU2IDAgMCAuMzAwNTYgMTguOTAyIDEuNzI4KSIgZmlsbD0iI2ZmZiIgc3Ryb2tlPSIjZmZmIi8+PC9zdmc+";
  const stalledIconSrc =
    "data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath d='M15.89 14.55l-7-14a1 1 0 00-1.78 0l-7 14a1 1 0 000 1A1 1 0 001 16h14a1 1 0 00.89-1.45zM7 6a1 1 0 012 0v3a1 1 0 01-2 0zm1 8a1 1 0 111-1 1 1 0 01-1 1z' fill='currentColor'%3E%3C/path%3E%3C/svg%3E";

  const isRisky = (route) => {
    return isRouteAtRiskARv2(route);
  };

  const getPartialIcon = () => {
    return (
      <img
        className="partial-icon"
        alt={"partial"}
        src={partialIconSrc}
        width="16px"
        height="16px"
      />
    );
  };

  const getStalledIcon = () => {
    return (
      <img
        className="stalled-icon"
        alt={"stalled"}
        src={stalledIconSrc}
        width="12px"
        height="12px"
      />
    );
  };

  const getSquareBox = (color, isPartial, height, width, isStalled) => {
    return (
      <Box
        minHeight={height}
        minWidth={width}
        maxHeight={height}
        maxWidth={width}
        backgroundColor={color}
      >
        {isPartial && getPartialIcon()}
        {isStalled && getStalledIcon()}
      </Box>
    );
  };

  const getSquareBoxWithBoundary = (color, isPartial, isStalled) => {
    return (
      <Box
        minHeight={boxHeight}
        minWidth={boxWidth}
        maxHeight={boxHeight}
        maxWidth={boxWidth}
        backgroundColor="#AD0A30"
        spacingInset="xxsmall"
      >
        {getSquareBox(color, isPartial, boxHeight - 4, boxWidth - 4, isStalled)}
      </Box>
    );
  };

  const renderRouteBox = (color, isAtRisk, isPartial, isStalled) => {
    return !isAtRisk
      ? getSquareBox(color, isPartial, boxWidth, boxHeight, isStalled)
      : getSquareBoxWithBoundary(color, isPartial, isStalled);
  };

  const getDefaultLegend = (color, labelId, defaultLabel) => {
    return (
      <Row wrap={"none"} spacing={"small"}>
        {renderRouteBox(color)}
        <Text>
          <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
        </Text>
      </Row>
    );
  };

  const getToolTipBody = (route) => {
    return (
      <Column className="tooltip-text" spacing={"none"}>
        <Row alignmentHorizontal={"center"}>
          <b>{route.routeCode}</b>
          <br />
        </Row>
        <Column spacing={"none"} alignmentHorizontal={"left"}>
          {getDisplayProgress(route)}
          {getStagingLocation(route)}
        </Column>
      </Column>
    );
  };

  const getStagingLocation = (route) => {
    return (
      <span style={{ textAlign: "left" }}>
        Location: {getToolTipTextFromList(route.stagingLocations)}
      </span>
    );
  };

  const getToolTipTextFromList = (list) => {
    if (Array.isArray(list) && list.length) {
      return list.map((element, index) => {
        return (
          <span key={element.displayLabel}>
            {" "}
            <b>
              {element.displayLabel}
              {index !== list.length - 1 ? "," : ""}
            </b>
            <br />
          </span>
        );
      });
    }
    return "...";
  };

  const renderVisualGridBoxes = (wave) => {
    return wave.routeList.map((route) => (
      <a
        key={route.routeId}
        onClick={() => {
          onRouteClick(route);
        }}
        style={{ textDecoration: "none", cursor: "pointer" }}
      >
        <Row className="html-tooltip" spacing={"none"} spacingInset={"none"}>
          {getToolTipBody(route)}
          {renderRouteBox(
            getRouteColor(route.overviewStatus),
            isRisky(route),
            route.isPartial,
            isRouteStalled(route, outboundConfigModel.stationConfig),
          )}
        </Row>
      </a>
    ));
  };

  return (
    <div>
      <Row
        widths={["grid-12"]}
        alignmentVertical="top"
        spacingInset={spacingInset_shorthand2}
      >
        <div>
          <FullScreen
            enabled={screenState}
            onChange={(enabled) => setScreenState(enabled)}
          >
            <div
              style={{
                overflow: "scroll",
                height: "100%",
                background: "none no-repeat scroll 0 0 #fff",
              }}
            >
              <Table headerRows={1} spacing={"small"}>
                <TableRow>
                  <TableCell alignmentHorizontal={"right"} width="130px">
                    <FormattedMessage
                      id="outboundARV2.wave"
                      defaultMessage="Stage by time"
                    />
                  </TableCell>
                  <TableCell alignmentHorizontal={"right"}>
                    <FormattedMessage
                      id="outboundAMZL.routesInCaps"
                      defaultMessage="Routes"
                    />
                  </TableCell>
                  <TableCell alignmentHorizontal={"left"}>
                    <Row
                      widths={["grid-10", "grid-2"]}
                      spacing="small"
                      wrap={"down"}
                      alignmentHorizontal={"left"}
                    >
                      <Row wrap={"down"} spacing={"none medium"}>
                        {getDefaultLegend(
                          getRouteColor(IN_PROGRESS),
                          "outboundARV2.inProgress",
                          IN_PROGRESS,
                        )}
                        {getDefaultLegend(
                          getRouteColor(PICKED),
                          "outboundARV2.picked",
                          PICKED,
                        )}
                        {getDefaultLegend(
                          getRouteColor(STAGED),
                          "outboundARV2.staged",
                          STAGED,
                        )}
                        {getDefaultLegend(
                          getRouteColor(DEPARTED),
                          "outboundARV2.departed",
                          DEPARTED,
                        )}
                        <Row wrap={"none"} spacing={"small"}>
                          {renderRouteBox("primary", true)}
                          <Text>
                            <FormattedMessage
                              id="outboundARV2.routeAtRisk"
                              defaultMessage={"Delayed pick"}
                            />
                          </Text>
                        </Row>
                        <Row wrap={"none"} spacing={"small"}>
                          <img
                            className="partial-icon"
                            alt={"partial"}
                            src={partialIconSrc}
                            width="16px"
                            height="16px"
                          />
                          <Text>
                            <FormattedMessage
                              id="outboundARV2.partial"
                              defaultMessage={"Partial"}
                            />
                          </Text>
                        </Row>
                      </Row>
                      <Row alignmentHorizontal={"right"} spacing={"none"}>
                        <Link onClick={toggleScreen} type="secondary">
                          <FormattedMessage
                            id="outboundAMZL.toggleFullscreen"
                            defaultMessage="Toggle fullscreen"
                          />
                        </Link>
                      </Row>
                    </Row>
                  </TableCell>
                </TableRow>

                {/*GRID*/}
                {routeWaveList.map((wave) => (
                  <TableRow key={wave.name}>
                    <TableCell alignmentHorizontal="right">
                      {wave.name}
                    </TableCell>
                    <TableCell alignmentHorizontal="right">
                      {wave.routeList.length}
                    </TableCell>
                    <TableCell>
                      <Row width="100%" spacing="small" wrap={"down"}>
                        {renderVisualGridBoxes(wave)}
                      </Row>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </div>
          </FullScreen>
        </div>
      </Row>
    </div>
  );
};

export default VisualGridViewARV2;



// WEBPACK FOOTER //
// ./src/components/outboundARv2/routegridview/VisualGridViewARV2.js